import { Button, Modal, ModalBody, ModalContent, ModalFooter, ModalHeader, Select, SelectItem, useDisclosure } from "@nextui-org/react";
import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { PlusIcon } from "@heroicons/react/24/outline";
import * as yup from "yup";
import toast from "react-hot-toast";
import { yupResolver } from "@hookform/resolvers/yup";
import useSWR from "swr";
import { getFetcher, postFetcher } from "../../../../services/api";
import useListCompany from "../../../../hooks/useListCompany";
import ControlledReactSelect from "../../../elements/ControlledReactSelect";
import useAuth from "../../../../hooks/useAuth";

export default function ModalAddMaterial({ endpoint, type, mutate, setSelectedCompany, setSelectedMaterial, setSelectedPeriod }) {
  const schema = yup.object().shape({
    companyId: yup.string().required("Harus diisi"),
    dataBahanB3Id: yup.string().required("Harus diisi"),
    periodId: yup.string().required("Harus diisi"),
  });
  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    register,
    formState: { isSubmitting, errors },
  } = useForm({ resolver: yupResolver(schema) });
  const companyId = watch("companyId");
  const periodId = watch("periodId");

  const { user, isSuperAdmin } = useAuth();

  /*
   * Pelapor penggunaan maupun distribusi B3 adalah perusahaan itu sendiri, jadi
   * kolom Perusahaan diisikan dari akun yang login dan dikunci. Super admin
   * dikecualikan - ia memang perlu mengisikan untuk perusahaan mana pun, dan
   * akunnya sengaja tidak tertaut perusahaan.
   */
  const perusahaanSendiri = user?.companies?.[0]?.company;
  const kunciPerusahaanSendiri = !isSuperAdmin && !!perusahaanSendiri?.id;

  const { isOpen, onOpen, onOpenChange, onClose } = useDisclosure();

  const { data: dataPeriods, isLoading: isLoadingPeriods } = useSWR(`/api/period/actives`, getFetcher);
  const { dataCompany, isLoadingCompany } = useListCompany();
  const { data: dataMaterial2, isLoading: isLoadingMaterial2 } = useSWR(companyId ? `/api/data-bahan-b3-company/search?companyIds=${companyId}&returnAll=true&hanyaAktif=true` : null, getFetcher);

  // Isi perusahaan sendiri begitu modal dibuka. Ditulis sebagai efek, bukan
  // defaultValue useForm, karena data pengguna dari AuthContext lazimnya baru
  // tersedia setelah render pertama.
  useEffect(() => {
    if (kunciPerusahaanSendiri && companyId !== perusahaanSendiri.id) {
      setValue("companyId", perusahaanSendiri.id);
    }
  }, [kunciPerusahaanSendiri, perusahaanSendiri?.id, companyId, setValue]);

  // Set default period when periods are loaded
  useEffect(() => {
    if (dataPeriods?.data && dataPeriods.data.length > 0 && !periodId) {
      setValue("periodId", dataPeriods.data[0].id);
    }
  }, [dataPeriods, periodId, setValue]);

  useEffect(() => {
    if (dataMaterial2 && companyId && dataMaterial2?.data?.length === 0) {
      toast.error("Tidak ada bahan b3 yang tersedia untuk perusahaan ini, harap tambahkan stock bahan b3 terlebih dahulu!");
    }
  }, [dataMaterial2, companyId]);

  function handleOnClose() {
    reset({
      // Perusahaan yang terkunci tidak dikosongkan saat modal ditutup - kalau
      // dikosongkan, efek di atas hanya akan mengisinya lagi dan sempat
      // membuat daftar bahan B3 berkedip kosong.
      companyId: kunciPerusahaanSendiri ? perusahaanSendiri.id : "",
      dataBahanB3Id: "",
      periodId: "",
    });
    onClose();
  }

  async function handleOnSubmit(data) {
    try {
      console.log(data);
      await postFetcher(endpoint, data);
      mutate();
      const company = dataCompany?.data?.find((item) => item.id === data.companyId);
      const period = dataPeriods?.data?.find((item) => item.id === data.periodId);

      setSelectedCompany({ value: company.id, label: company.name });
      setSelectedPeriod({ value: period.id, label: period.name });
      setSelectedMaterial(data.dataBahanB3Id);
      toast.success("Laporan bahan b3 berhasil ditambah!");
      handleOnClose();
    } catch (error) {
      const msg = error?.response?.data?.message ?? "Terjadi kesalahan";
      console.error(error);
      if (msg) toast.error(msg);
      else toast.error("Gagal tambah laporan bahan b3!");
    }
  }

  return (
    <>
      <Button size="sm" onPress={onOpen} color="primary" startContent={<PlusIcon className="size-4" />}>
        Tambah Bahan B3
      </Button>
      <Modal isOpen={isOpen} onOpenChange={onOpenChange} onClose={handleOnClose} isDismissable={false} isKeyboardDismissDisabled={false}>
        <ModalContent>
          {(onClose) => (
            <form onSubmit={handleSubmit(handleOnSubmit)}>
              <ModalHeader className="flex flex-col gap-1">Tambah Laporan {type} Bahan B3</ModalHeader>
              <ModalBody className="flex flex-col gap-3">
                <Select
                  {...register("periodId")}
                  variant="faded"
                  isRequired
                  disallowEmptySelection
                  label="Periode"
                  labelPlacement="outside"
                  placeholder="Pilih Periode..."
                  color={errors.periodId ? "danger" : "default"}
                  isInvalid={errors.periodId}
                  errorMessage={errors.periodId && errors.periodId.message}
                  isLoading={isLoadingPeriods}
                >
                  {dataPeriods?.data?.map((period) => (
                    <SelectItem key={period.id} value={period.id}>
                      {period.name}
                    </SelectItem>
                  ))}
                </Select>

                <ControlledReactSelect
                  control={control}
                  name="companyId"
                  label="Perusahaan"
                  options={dataCompany?.data?.map((item) => ({ value: item.id, label: item.name }))}
                  isLoading={isLoadingCompany}
                  isDisabled={kunciPerusahaanSendiri}
                />
                <ControlledReactSelect
                  control={control}
                  name="dataBahanB3Id"
                  label="Bahan B3"
                  options={dataMaterial2?.data?.map((item) => ({ value: item?.dataBahanB3?.id, label: `${item?.dataBahanB3?.casNumber} - ${item?.dataBahanB3?.namaBahanKimia}` }))}
                  isLoading={isLoadingMaterial2}
                />
              </ModalBody>
              <ModalFooter>
                <Button isLoading={isSubmitting} isDisabled={isSubmitting} color="danger" variant="light" onPress={onClose}>
                  Tutup
                </Button>
                <Button isLoading={isSubmitting} isDisabled={isSubmitting} type="submit" color="primary">
                  Simpan
                </Button>
              </ModalFooter>
            </form>
          )}
        </ModalContent>
      </Modal>
    </>
  );
}
